/**
 * Canonical wire contract for ONE voucher's flight status — the single report
 * the cirium-integration projects per processed alert, and the single document
 * customer-api keeps per voucher.
 *
 * WHY THIS SUPERSEDES `FlightLiveStatusReport` + `FlightChangeEvent`. Those two
 * shipped as siblings: one the mutable snapshot of what IS ("the gate is 217"),
 * the other the append-only log of what CHANGED ("the gate moved from 15 to
 * 217"). They are genuinely different questions — folding the log cannot answer
 * the first (a gate announced once and never moved is not a change under a pull
 * provider, which computes no diff), and the snapshot cannot answer the second
 * (the previous value is gone the moment it is overwritten). But two producers
 * writing two collections on every alert, for one voucher, is one write and one
 * read too many for the product that consumes both. So the two shapes fold into
 * ONE report:
 *
 *   `legs`    → STATE. Whole-voucher replace, because the producer always knows
 *               the full monitored set and a patch would strand a leg that a
 *               re-imported itinerary no longer contains.
 *   `changes` → HISTORY. Append-only within the document, deduped by
 *               `eventKey`, each entry keeping the `from` that only the producer
 *               ever sees and the `notificationOutcome` that only the deciding
 *               instant knows.
 *
 * `changes` lives at the VOUCHER level, not inside a leg: an alert can be about
 * the itinerary rather than one flight (`CONNECTION_AT_RISK` names no leg), and
 * a leg-nested array would leave those orphaned. Each entry carries its own leg
 * identity instead.
 *
 * ONE IDENTITY VOCABULARY. State and history describe the same legs, so both
 * reuse {@link FlightLegIdentitySchema} — which also settles a divergence the
 * two old shapes carried: the live report spelled `flightNumber` as a string
 * (the two sides write `1420` vs `G3 1420`), the change event as a number.
 * String wins, for the reason the live report gave.
 */
import { z } from 'zod';

import { EnvelopeOrgIdSchema } from '../conversation/org_id';
import { FlightChangeDetailSchema, FlightChangeNotificationOutcomeSchema } from '../flight_change';
import { FlightLiveStatusSchema } from '../traveler/traveler_view';

/**
 * How to RECOGNISE one monitored leg.
 *
 * Every field is nullable because the producer may know a leg only partially; a
 * matcher is expected to require agreement on what IS present and to refuse a
 * candidate that agrees on nothing. Positional index is deliberately NOT the
 * identity across services: the two sides derive their leg lists independently
 * from the same voucher (the producer from its trip import, the store from the
 * mirrored segments), so an index would look right in every test and drift the
 * first time one side filters or reorders.
 */
export const FlightLegIdentitySchema = z
  .object({
    /** Airline code as the producer knows it (may be an FS code, not IATA). */
    carrier: z.string().min(1).nullable(),
    /** Digits as a STRING — the two sides spell it differently (`1420` vs `G3 1420`). */
    flightNumber: z.string().min(1).nullable(),
    departureAirport: z.string().min(1).nullable(),
    arrivalAirport: z.string().min(1).nullable(),
    /**
     * Airport-local wall clock of the scheduled departure, offset-less ISO —
     * the same shape `FlightSegment.departLocal` carries, so the two compare
     * without either side re-deriving a zone.
     */
    scheduledDepartureLocal: z.string().min(1).nullable(),
  })
  .strict();
export type FlightLegIdentity = z.infer<typeof FlightLegIdentitySchema>;

/** One monitored leg: how to recognise it, plus its CURRENT state. */
export const FlightStatusLegSchema = FlightLegIdentitySchema.extend({
  live: FlightLiveStatusSchema,
}).strict();
export type FlightStatusLeg = z.infer<typeof FlightStatusLegSchema>;

/**
 * One recorded change — a fact, not a state.
 *
 * `eventKey` is the producer's content-derived dedup identity (cirium hashes
 * the alert id with the change type and side). The store keys the append on it,
 * so an at-least-once redelivery collapses instead of duplicating, and a change
 * that already landed is never re-appended.
 *
 * `leg` is null when the alert is about the itinerary rather than one flight.
 *
 * `flightIndex` is the producer's index into the VOUCHER's own leg list — NOT a
 * position in this report's `legs`, which carries only the legs that have been
 * observed and therefore renumbers. It is useful for grouping entries of the
 * same flight; the join key is the identity above, never the index.
 */
export const FlightStatusChangeSchema = z
  .object({
    eventKey: z.string().min(1).max(200),
    leg: FlightLegIdentitySchema.nullable(),
    flightIndex: z.number().int().nonnegative().nullable(),
    change: FlightChangeDetailSchema,
    /** True instant the provider recorded the event (the alert `triggeredAt`). */
    occurredAt: z.coerce.date(),
    /** What the producer decided about contacting the traveler, at that instant. */
    notificationOutcome: FlightChangeNotificationOutcomeSchema,
    alertId: z.string().min(1),
    alertType: z.string().min(1),
  })
  .strict();
export type FlightStatusChange = z.infer<typeof FlightStatusChangeSchema>;

export const FlightStatusReportSchema = z
  .object({
    /** The voucher this report is about — the store keys the row on it. */
    voucherId: z.string().min(1).max(200),
    voucherCode: z.string().min(1).max(200),
    /** Customer public id (opaque UUID) — never the legacy numeric id. */
    customerId: z.string().min(1).max(200),
    orgId: EnvelopeOrgIdSchema,
    /**
     * The instant the producer's view was current — the ORDERING key, and the
     * reason the store can be safe about at-least-once delivery.
     *
     * `legs` is a snapshot of the producer's whole view, not a delta, so it
     * orders as a unit and the store refuses a report older than what it holds.
     * Without this, a retry that overtakes a newer report silently rolls the
     * gate back, and nothing downstream could tell: the store's own clock says
     * only when a payload ARRIVED, never when it was true. `changes` is exempt
     * from the guard — an append is order-independent and deduped by key.
     *
     * Distinct from the per-leg `live.observedAt`, which says when a particular
     * leg was last seen and lets a consumer refuse stale VALUES.
     */
    observedThrough: z.preprocess((v) => v ?? undefined, z.coerce.date()),
    /** Empty is legal and meaningful: monitored, nothing observed on any leg yet. */
    legs: z.array(FlightStatusLegSchema),
    /** Empty is the common case: this alert changed nothing worth recording. */
    changes: z.array(FlightStatusChangeSchema),
    source: z.object({ provider: z.enum(['flex_alerts', 'sky', 'trips_waivers']) }).strict(),
  })
  .strict();
export type FlightStatusReport = z.infer<typeof FlightStatusReportSchema>;
